import React, {useEffect, useRef, useState} from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSnackbar } from "notistack";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import {useCenteredSnackbar} from "../../tools/useCenteredSnackbar";

const AddLastMinute = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar,closeSnackbar } = useSnackbar();
  const { showCenteredPopup } = useCenteredSnackbar();
  const [canAddAd, setCanAddAd] = useState(false);
  const [logo, setLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCanAdd, setIsLoadingCanAdd] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);


  const action = key => (
      <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => closeSnackbar(key)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
  );
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    last_minute_button: "",
    last_minute_url: "",
    count_hours_subscriptions: 24, // Default to 24 hours
  });

  // Error states
  const [errors, setErrors] = useState({
    title: false,
    description: false,
    last_minute_button: false,
    last_minute_url: false,
    count_hours_subscriptions: false,
    logo: false,
  });

  const handleLogoUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setLogo(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    setLogo(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!captchaToken) {
      showCenteredPopup("Please complete the CAPTCHA",
          { variant: "error",
            persist: true });
      // enqueueSnackbar("Please complete the CAPTCHA", {variant: "error",
      //   persist: true,
      //   action,});
      return;
    }
    setIsLoading(true);

    // Validate all required fields
    const newErrors = {
      title: !formData.title.trim(),
      description: !formData.description.trim(),
      last_minute_button: !formData.last_minute_button.trim(),
      last_minute_url: !formData.last_minute_url.trim(),
      count_hours_subscriptions: !formData.count_hours_subscriptions ||
          formData.count_hours_subscriptions <= 0,
      logo: !logo,
    };

    setErrors(newErrors);

    // Check if any errors exist
    if (Object.values(newErrors).some(error => error)) {
      showCenteredPopup("Please fill all required fields",
          { variant: "error",
            persist: true });
      // enqueueSnackbar("Please fill all required fields", {
      //   variant: "error",
      //   persist: true,
      //   action,
      //   anchorOrigin: {
      //     vertical: "top",
      //     horizontal: "right",
      //   },
      // });
      setIsLoading(false);
      return;
    }

    // Prepare form data
    const data = new FormData();
    if (logo) data.append("logo", logo);

    Object.entries(formData).forEach(([key, value]) => {
      data.append(key, value);
    });

    // Use Helper.Post as is
    const { response, message } = await Helper.Post({
      url: api_Routes.LastMinute.createAd,
      hasToken: true,
      data: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response) {
      navigate("/LastMinute");
    } else {
      // Handle API errors
      if (typeof message === "string") {
        showCenteredPopup(message,
            { variant: "error",
              persist: true });
        // Simple string error message
        // enqueueSnackbar(message, {
        //   variant: "error",
        //   persist: true,
        //   action,
        //   anchorOrigin: {
        //     vertical: "top",
        //     horizontal: "right",
        //   },
        // });
      } else if (message?.errors) {
        // Laravel-style validation errors
        Object.values(message.errors).flat().forEach(errorMessage => {
          showCenteredPopup(errorMessage,
              { variant: "error",
                persist: true });
          // enqueueSnackbar(errorMessage, {
          //   variant: "error",
          //   persist: true,
          //   action,
          //   anchorOrigin: {
          //     vertical: "top",
          //     horizontal: "right",
          //   },
          // });
        });
      } else if (message?.message) {
        // General error message
        showCenteredPopup(message.message,
            { variant: "error",
              persist: true });
        // enqueueSnackbar(message.message, {
        //   variant: "error",
        //   persist: true,
        //   action,
        //   anchorOrigin: {
        //     vertical: "top",
        //     horizontal: "right",
        //   },
        // });
      } else {
        showCenteredPopup("An error occurred. Please try again.",
            { variant: "error",
              persist: true });
        // Fallback error message
        // enqueueSnackbar("An error occurred. Please try again.", {
        //   variant: "error",
        //   persist: true,
        //   action,
        //   anchorOrigin: {
        //     vertical: "top",
        //     horizontal: "right",
        //   },
        // });
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const get_Can_add_ad = async () => {
      setIsLoadingCanAdd(true);
      const { response, message } = await Helper.Get({
        url: api_Routes.LastMinute.canAdd,
        hasToken: true,
      });
      if (response?.data) {
        setCanAddAd(response.data);
      }
      setIsLoadingCanAdd(false);
    };
    get_Can_add_ad();
  }, []);

  if (isLoadingCanAdd) return <LinearProgress />;

  return (
      <>
        {canAddAd ? (
            <Box sx={{ maxWidth: 800, margin: "auto", p: 3 }}>
              <Typography variant="h4" gutterBottom>
                Create New Last Minute
              </Typography>

              {/* Title */}
              <TextField
                  fullWidth
                  label="Last Minute Title *"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  error={errors.title}
                  helperText={errors.title && "Title is required"}
                  sx={{ mb: 3 }}
              />

              {/* Description */}
              <TextField
                  fullWidth
                  label="Description *"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={4}
                  error={errors.description}
                  helperText={errors.description && "Description is required"}
                  sx={{ mb: 3 }}
              />

              {/* Button Title */}
              <TextField
                  fullWidth
                  label="Button Title *"
                  name="last_minute_button"
                  value={formData.last_minute_button}
                  onChange={handleFormChange}
                  error={errors.last_minute_button}
                  helperText={errors.last_minute_button && "Button title is required"}
                  sx={{ mb: 3 }}
              />

              {/* Button Link */}
              <TextField
                  fullWidth
                  label="Button Link it must include (http:// or https://) *"
                  name="last_minute_url"
                  value={formData.last_minute_url}
                  onChange={handleFormChange}
                  error={errors.last_minute_url}
                  helperText={errors.last_minute_url && "Valid URL is required"}
                  sx={{ mb: 3 }}
              />

              {/* Display Duration */}
              <TextField
                  fullWidth
                  label="Display Duration (hours) *"
                  name="count_hours_subscriptions"
                  type="number"
                  value={formData.count_hours_subscriptions}
                  onChange={handleFormChange}
                  error={errors.count_hours_subscriptions}
                  helperText={errors.count_hours_subscriptions && "Must be a positive number"}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 3 }}
              />

              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                {errors.logo && (
                    <Typography color="error" variant="caption" display="block" gutterBottom>
                      Photo is required
                    </Typography>
                )}

                <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    id="logo-upload"
                    style={{ display: "none" }}
                />
                <label htmlFor="logo-upload">
                  <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={{ mb: 2 }}
                  >
                    Upload Photo *
                  </Button>
                </label>

                {/* Photo Preview */}
                {logo && (
                    <Box sx={{ position: "relative", width: 200, mt: 2 }}>
                      <IconButton
                          size="small"
                          onClick={removeLogo}
                          sx={{
                            position: "absolute",
                            right: -10,
                            top: -10,
                            backgroundColor: "error.main",
                            color: "white",
                            "&:hover": { backgroundColor: "error.dark" },
                            zIndex: 1,
                          }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <img
                          src={URL.createObjectURL(logo)}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: 200,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                      />
                    </Box>
                )}
              </Box>
              <ReCAPTCHA
                  sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  ref={recaptchaRef}
              />

              {/* Submit Button */}
              <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  fullWidth
                  size="large"
              >
                {isLoading ? "Creating Last Minute..." : "Create Last Minute"}
              </Button>
            </Box>
        ) : (
            // Existing limit reached message (same as before)
            <Box sx={{ marginTop: "20px" }}>
              <Typography
                  sx={{
                    fontSize: "40px",
                    lineHeight: "45px",
                    fontWeight: "700",
                    color: "#474747",
                    textAlign: "center",
                  }}
              >
                Sorry you rich to limit of posting ad this month
              </Typography>
              <Typography
                  sx={{
                    fontSize: "18px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#474747",
                    textAlign: "center",
                    marginTop: "30px",
                  }}
              >
                Please contact with admin or buy service of posting add by clicking here
                <Box sx={{ marginTop: 5, justifySelf: "center" }}>
                  <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#3F78E0",
                        color: "#F1F5FD",
                        borderRadius: "12px",
                        "&:hover": { backgroundColor: "#355dab" },
                        border: "2px solid #3F78E0",
                        display: "flex",
                        gap: "10px",
                        padding: "10px 12px",
                        boxShadow: "0px 4px 4px #00000040",
                      }}
                      onClick={() => navigate("/OurServices")}
                  >
                    <Typography
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: "500",
                          fontSize: "16px",
                          color: "#F1F5FD",
                          whiteSpace: "nowrap",
                        }}
                    >
                      Buy More Ads
                    </Typography>
                    <svg
                        className="sign-up-arrow"
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                          d="M11.2441 2.8457H14.0653C14.4394 2.8457 14.7982 2.99432 15.0627 3.25885C15.3273 3.52339 15.4759 3.88218 15.4759 4.25629V14.1304C15.4759 14.5045 15.3273 14.8633 15.0627 15.1278C14.7982 15.3924 14.4394 15.541 14.0653 15.541H11.2441"
                          stroke="white"
                          strokeWidth="1.41059"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                      />
                      <path
                          d="M7.7168 12.7199L11.2433 9.19346L7.7168 5.66699"
                          stroke="white"
                          strokeWidth="1.41059"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                      />
                      <path
                          d="M11.2428 9.19336H2.7793"
                          stroke="white"
                          strokeWidth="1.41059"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </Box>
              </Typography>
            </Box>
        )}
      </>
  );
};

export default AddLastMinute;